/**
 * THE SHOP'S FOUR ACTION BUTTONS, DECLARED ONCE.
 *
 * Share, follow, call and WhatsApp used to be written out inline in
 * `ShopHero.js`. Round 15 moves share and the contact channels off the hero
 * and into the side panel, and the owner was explicit that the panel must
 * REUSE what already exists ("we already have all the buttons, just use
 * them"). Two copies of a button is how the panel ends up dialling the salon
 * phone while the hero dials the owner's call override — so there is one
 * copy, and both surfaces import it.
 *
 * Each button takes its own class, because the two surfaces dress them
 * differently (glass circle on the cover, glass circle with a label in the
 * panel) — but WHAT they do, which number they use, and which owner setting
 * gates them is decided here, in one place.
 */
import React from 'react';
import { countryCodes, findCountryByPhone, getCountryByCode } from '../../utils/countryCodes';
import { countryCodeOf } from '../../utils/countryIdentity';
import ShareIcon from '../../Components/ShareIcon';
import phoneIcon from '../../assets/phone.svg';
import whatsappIcon from '../../assets/whatsapp.png';
import heartIcon from '../../assets/heart.svg';
import heartOutlineIcon from '../../assets/heart-outline.svg';
import WhatsAppLink from '../../Components/WhatsAppLink/WhatsAppLink';

/**
 * `wa.me` NEEDS A FULL INTERNATIONAL NUMBER — WITH THE SHOP'S OWN COUNTRY IN IT.
 *
 * This used to prefix 972 to anything that did not already start with it, which was true of the shops it was
 * written for and false of the ones Stylon opened afterwards: a Jordanian shop's `+962 79…` became
 * `wa.me/972962…`, a number nobody has, on every surface that offers WhatsApp.
 *
 * What is trusted, in order: a number written in international form ('+' or '00') is left alone; one that already
 * opens with the business's own dial code is left alone; one long enough to be international AND opening with some
 * country's code is left alone (a number stored without its '+'); anything else is local and takes the dial code of
 * the business's own `country`, however that country was written down. Israel is the fallback when nobody recorded
 * one, which is what every older shop relies on. A SHORT number is never read as international: a local Israeli
 * `052…` opens with Mexico's +52.
 *
 * Returns null when there is no usable number, so the CALLER can decide not to render rather than rendering a dead
 * link. `text` writes the first line of the message, for the surfaces that open WhatsApp with one.
 */
/**
 * THE DIAL CODE OF A COUNTRY AS A BUSINESS ACTUALLY WROTE IT DOWN — digits, or null.
 *
 * `salon.country` is free text: production holds 'IL', 'Israel', 'ישראל' and, for the businesses Stylon opened
 * afterwards, whatever the admin panel was given. `countryCodeOf` (utils/countryIdentity.js) settles the spellings
 * it knows, the ISO-2 table answers the codes, and the country NAMES answer the rest. Identity only — a dial code
 * decides how a number is written, never what a business may do.
 */
function dialDigitsOf(country) {
  const stored = countryCodeOf(country);
  if (!stored) return null;
  const byCode = getCountryByCode(stored);
  if (byCode) return byCode.dialCode.replace(/\D/g, '');
  const byName = countryCodes.find((c) => c.name.toUpperCase() === stored);
  return byName ? byName.dialCode.replace(/\D/g, '') : null;
}

/**
 * A number long enough to carry a country code in front of a real subscriber number. A local number — ten digits
 * with its trunk zero, nine without — never reaches this, so an Israeli 052… is not read as Mexico's +52.
 */
const INTERNATIONAL_MIN_DIGITS = 11;

export function getWhatsAppUrl(phone, country, text) {
  const raw = (phone == null ? '' : String(phone)).trim();
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const said = text ? `?text=${encodeURIComponent(text)}` : '';
  if (raw.startsWith('+')) return `https://wa.me/${digits}${said}`;
  if (digits.startsWith('00')) return `https://wa.me/${digits.slice(2)}${said}`;
  const dial = dialDigitsOf(country) || '972';
  if (digits.startsWith(dial)) return `https://wa.me/${digits}${said}`;
  // Stored without its '+' but long enough to be international, and opening with a country's code: an Israeli
  // number on a business recorded in Jordan must not be given a second one.
  if (digits.length >= INTERNATIONAL_MIN_DIGITS && findCountryByPhone(digits)) return `https://wa.me/${digits}${said}`;
  return `https://wa.me/${dial}${digits.replace(/^0+/, '')}${said}`;
}

/**
 * THE NUMBER A DIALLER CAN TAKE (2026-10-14) — `+<country code><subscriber>`, digits only, or '' when there is
 * nothing to dial.
 *
 * The owner, inside Instagram on his own phone: the Call on the "WhatsApp didn't open?" sheet did nothing. `tel:`
 * carried the number exactly as the shop had typed it into the portal — '052-123-4567', '03-5551234', sometimes
 * with spaces — and a `tel:` an in-app web view cannot read as a number is a link that opens nothing.
 *
 * It is built from `getWhatsAppUrl` on purpose rather than beside it: the dialler and WhatsApp then address the
 * SAME number, under the same country rules (a Jordanian 079… gets +962, an Israeli 0… gets +972, an already
 * international number is left alone), and a country rule can never be fixed in one of the two and not the other.
 */
export function dialablePhone(phone, country) {
  const url = getWhatsAppUrl(phone, country);
  const m = url ? /^https:\/\/wa\.me\/(\d+)/.exec(url) : null;
  return m ? `+${m[1]}` : '';
}

/**
 * WHICH CHANNELS THIS BUSINESS OFFERS — the single answer both surfaces read.
 *
 * The server sends effective `callNumber` / `whatsappNumber` (already falling
 * back to the salon phone); the extra `??` chain keeps older/cached API
 * shapes working. `heroShow*` are the owner's own visibility settings, and
 * `!== false` is deliberate: an unset flag means "show", which is what every
 * existing shop has stored.
 */
export function shopChannels(salon) {
  const callPhone = salon?.callNumber ?? salon?.contactNumber ?? salon?.phone ?? '';
  const waPhone = salon?.whatsappNumber ?? salon?.contactNumber ?? salon?.phone ?? '';
  const whatsappUrl = getWhatsAppUrl(waPhone, salon?.country);
  // The checkout reads these on every render of its payment step; a non-string here must not throw into it.
  const instagram = typeof salon?.instagram === 'string' ? salon.instagram.trim() : '';
  const instagramHandle = instagram.split('/').pop() || null;
  // What a `tel:` must carry on EVERY surface (2026-10-14) — the words a row prints stay `callPhone`, the shop's
  // own spelling; the link is this. A business whose "phone" is not a number has nothing to dial, so no button.
  const callTel = dialablePhone(callPhone, salon?.country);
  return {
    callPhone,
    callTel,
    whatsappUrl,
    instagramHandle,
    showCall: salon?.heroShowCall !== false && !!callTel,
    // Floating means moved, not copied — see AboutSec.
    showWhatsapp: salon?.heroShowWhatsapp !== false && !!whatsappUrl && salon?.floatingWhatsappEnabled !== true,
    showInstagram: salon?.heroShowInstagram !== false && !!instagram,
    showNavigation: salon?.heroShowNavigation !== false,
  };
}

/** A mask-painted glyph, so the icon takes `currentColor` from its button and
 *  can therefore be whatever the measured foreground for this page is. */
export const MaskGlyph = ({ src, className }) => (
  <span
    className={className}
    style={{ maskImage: `url(${src})`, WebkitMaskImage: `url(${src})` }}
    aria-hidden
  />
);

export function ShareButton({ className, iconClassName, salonName, t, testId = 'shop-share', children }) {
  const onShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = salonName || 'Shop';
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* user cancelled */ }
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    }
  };
  return (
    <button
      type="button"
      className={className}
      onClick={onShare}
      data-testid={testId}
      aria-label={t('share', { defaultValue: 'Share' })}
    >
      <ShareIcon size={20} className={iconClassName} />
      {children}
    </button>
  );
}

export function FollowButton({
  className, iconClassName, liked, busy, onToggle, t, testId = 'shop-store-heart', children,
}) {
  return (
    <button
      type="button"
      className={className}
      onClick={onToggle}
      disabled={busy}
      aria-pressed={!!liked}
      aria-label={liked
        ? t('shop.unlikeStore', { defaultValue: 'Remove shop from favorites' })
        : t('shop.likeStore', { defaultValue: 'Add shop to favorites' })}
      data-testid={testId}
    >
      <MaskGlyph src={liked ? heartIcon : heartOutlineIcon} className={iconClassName} />
      {children}
    </button>
  );
}

/**
 * `country` IS NOT OPTIONAL DECORATION (2026-10-14): it is how a local number becomes dialable. `dialablePhone`
 * is idempotent, so a caller handing over `channels.callTel` rather than `channels.callPhone` gets the same
 * answer — what no caller can do any more is dial the shop's own spelling.
 */
export function CallButton({ className, iconClassName, phone, country, t, testId = 'shop-call', children }) {
  return (
    <a
      href={`tel:${dialablePhone(phone, country)}`}
      className={className}
      data-testid={testId}
      aria-label={t('shop.call', { defaultValue: 'Call' })}
    >
      <MaskGlyph src={phoneIcon} className={iconClassName} />
      {children}
    </a>
  );
}

export function WhatsappButton({ className, iconClassName, url, testId = 'shop-whatsapp', children }) {
  return (
    <WhatsAppLink href={url} className={className} testId={testId} ariaLabel="WhatsApp">
      <MaskGlyph src={whatsappIcon} className={iconClassName} />
      {children}
    </WhatsAppLink>
  );
}
